import React, { memo, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Outlet, useLocation } from 'react-router-dom'
import { readSideNavCollapsed, writeSideNavCollapsed } from 'src/utils/sideNavCollapsed'
import { TopNav } from './TopNav'
import { ShellErrorBoundary } from './ShellErrorBoundary'
import { SideNavContext } from './SideNav.context'
import type { SideNavContextValue } from './SideNav.context'
import { NavigationGuardContext } from './NavigationGuard.context'
import type { GuardedNavigate, NavigationGuardContextValue } from './NavigationGuard.context'

/**
 * The top bar spans the full viewport width; under it, the side rail on the left and the page on
 * the right. Pages render their `SideNav` from inside the outlet and it portals into the left
 * slot — an empty slot has no width, so a page without a rail (the launcher) gets full-width
 * content.
 */
const AdminShellComponent = () => {
  const location = useLocation()

  // State rather than a ref so `SideNav` re-renders once the slot exists — a ref would be
  // populated after the children's first render and the portal would never mount.
  const [sideNavSlot, setSideNavSlot] = useState<HTMLElement | null>(null)
  const [isSideNavCollapsed, setIsSideNavCollapsed] = useState<boolean>(readSideNavCollapsed)
  const [hasSideNav, setHasSideNav] = useState<boolean>(false)
  const toggleSideNavCollapsed = useCallback(() => {
    setIsSideNavCollapsed((previous) => {
      const next = !previous

      writeSideNavCollapsed(next)

      return next
    })
  }, [])

  const [guardedNavigate, setGuardedNavigate] = useState<GuardedNavigate | null>(null)

  const navigationGuardContextValue = useMemo<NavigationGuardContextValue>(() => {
    return { guardedNavigate, setGuardedNavigate }
  }, [guardedNavigate])

  const sideNavContextValue = useMemo<SideNavContextValue>(() => {
    return {
      slot: sideNavSlot,
      isCollapsed: isSideNavCollapsed,
      hasSideNav,
      toggleCollapsed: toggleSideNavCollapsed,
      setHasSideNav,
    }
  }, [sideNavSlot, isSideNavCollapsed, hasSideNav, toggleSideNavCollapsed])

  return (
    <SideNavContext.Provider value={sideNavContextValue}>
      <NavigationGuardContext.Provider value={navigationGuardContextValue}>
        <div data-testid="admin-shell" css={tw`flex h-screen w-full flex-col overflow-hidden`}>
          <TopNav />
          <div css={tw`flex min-h-0 flex-1 flex-row`}>
            <div ref={setSideNavSlot} data-testid="admin-side-nav-slot" css={tw`flex shrink-0`} />
            <div css={tw`flex min-w-0 flex-1 flex-row bg-shades-50`}>
              <ShellErrorBoundary key={location.pathname}>
                <Outlet />
              </ShellErrorBoundary>
            </div>
          </div>
        </div>
      </NavigationGuardContext.Provider>
    </SideNavContext.Provider>
  )
}

export const AdminShell = memo(AdminShellComponent)
